import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import axios from 'axios';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const ScoreHistoryChart = ({ currentScore, projectName }) => {
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: []
  });
  const [hasHistory, setHasHistory] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const API_URL = process.env.NODE_ENV === 'production' 
          ? '/api' 
          : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');
        
        const token = localStorage.getItem('authToken');
        
        // If no project name, don't fetch
        if (!projectName) return;

        const response = await axios.get(`${API_URL}/projects/${encodeURIComponent(projectName)}/history`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        const history = response.data || [];
        
        // If history has 1 or fewer items, don't show the chart
        if (history.length <= 1) {
          setHasHistory(false);
          return;
        }
        
        setHasHistory(true);
        
        const dates = history.map(item => new Date(item.date));
        const scores = history.map(item => item.score);

        // Format dates
        const formatDate = (date) => {
          return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        };

        setChartData({
          labels: dates.map(formatDate),
          datasets: [
            {
              label: 'Score',
              data: scores,
              borderColor: '#1850ff',
              backgroundColor: 'rgba(24, 80, 255, 0.1)',
              borderWidth: 3,
              fill: true,
              tension: 0.4,
              pointRadius: 5,
              pointHoverRadius: 7,
              pointBackgroundColor: '#fff',
              pointBorderColor: '#1850ff',
              pointBorderWidth: 2,
            }
          ]
        });

      } catch (error) {
        console.error('Error fetching history:', error);
        // Hide chart on error
        setHasHistory(false);
      }
    };

    fetchData();
  }, [projectName, currentScore]);

  if (!hasHistory) {
    return null;
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#121b29',
        bodyColor: '#27303e',
        borderColor: '#dce2ec',
        borderWidth: 1,
        padding: 12,
        titleFont: {
          size: 14,
          weight: 'bold'
        },
        bodyFont: {
          size: 12
        },
        callbacks: {
          label: function(context) {
            return `Score: ${context.parsed.y.toFixed(1)} / 5.0`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 5.0,
        ticks: {
          color: '#6b788d',
          font: {
            size: 12
          },
          stepSize: 0.5
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        }
      },
      x: {
        ticks: {
          color: '#6b788d',
          font: {
            size: 11
          },
          maxRotation: 45,
          minRotation: 45
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        }
      }
    }
  };

  return (
    <div className="p-6 m-6 bg-white border border-gray-200 rounded-lg shadow-sm dark:border-gray-700 dark:bg-gray-800 text-center">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-5">Score History</h3>
      <div className="w-full h-[400px] flex items-center justify-center transition-transform duration-200 hover:scale-[1.01]">
        <Line data={chartData} options={chartOptions} />
      </div>
    </div>
  );
};

export default ScoreHistoryChart;
